import { Plus } from "lucide-react";
import { cn } from "../../utils/tailwindUtils";
import { Button } from "./button";
import male from "../../assets/svg/Male.svg";

/** Shared page surface — opaque in light mode so content doesn't sit on the bg illustration. */
export const PAGE_SURFACE_CLASS =
    "bg-white/95 dark:bg-gray-900 rounded-lg shadow-sm border border-gray-200/80 dark:border-gray-800 dark:shadow-md";

export const ContainerCard = ({ children, className, scrollable = false }) => {
    return (
        <div
            className={cn(
                "p-2 flex flex-col flex-1 min-h-0",
                scrollable ? "overflow-y-auto" : "overflow-hidden",
                PAGE_SURFACE_CLASS,
                className,
            )}
        >
            {children}
        </div>
    );
};

export const ModuleTitleCard = ({
    Icon,
    title,
    subtitle,
    totalRecords,
    buttonEnable = false,
    setOpenDialog,
    buttonName = "Add New",
}) => {
    return (
        <div className="flex flex-row justify-between items-center md:items-start gap-4 mb-1 p-3 rounded-lg shrink-0">
            {/* Left Section - Title and Info */}
            <div className="flex-1">
                <div className="flex items-center gap-3">
                    <Icon className="w-6 h-6 text-sidebar-primary flex-shrink-0" />
                    <div className="min-w-0">
                        <h1 className="text-xl font-semibold text-gray-900 dark:text-white truncate">
                            {title}
                            {totalRecords !== undefined && totalRecords !== null && (
                                <span className="text-muted-foreground font-normal ml-2">
                                    ({totalRecords})
                                </span>
                            )}
                        </h1>

                        {/* Subtitle - Hidden on mobile */}
                        {subtitle && (
                            <p className="hidden sm:block text-sm text-muted-foreground mt-1 line-clamp-2">
                                {subtitle}
                            </p>
                        )}
                    </div>
                </div>
            </div>

            {/* Right Section - Button */}
            {buttonEnable && (
                <Button
                    size="sm"
                    variant="brand"
                    className={cn(
                        "transition-colors duration-200",
                        "shadow-sm hover:shadow-md",
                        "whitespace-nowrap",
                        "px-2 sm:px-3" // Smaller padding on mobile
                    )}
                    onClick={() => setOpenDialog?.(true)}
                    aria-label={`Add new ${title}`}
                >
                    <Plus className="w-4 h-4 sm:mr-2" />
                    {/* Button text hidden on mobile, shown on sm and above */}
                    <span className="hidden sm:inline">{buttonName}</span>
                </Button>
            )}
        </div>
    );
};

// employee profile
export const EmployeeDetails = ({
    details,
    icon,
    isFromSubordinate,
    profileWrap,
    isFromInbox,
    is_active = true,
    size,
    loading = false, // Added loading prop
    onClick
}) => {
    const fullname = `${details?.title ? details.title : ""} ${details?.first_name ? details.first_name : ""} ${details?.last_name ? details.last_name : ""}`.trim();
    const profile = details?.profile ? details.profile : male;
    const designation = details?.designation_id?.designation_name ? details.designation_id.designation_name : "";
    const employee_code = details?.employee_id || details?.id || "";

    // Determine avatar size class based on props
    const getAvatarSizeClass = () => {
        if (isFromSubordinate) return "w-8 h-8";
        if (isFromInbox) return "w-10 h-10";
        return "w-10 h-10";
    };

    // Determine text size class based on props
    const getNameSizeClass = () => {
        if (isFromSubordinate) return "text-sm";
        if (isFromInbox) return "text-base";
        return "text-base";
    };

    const getDesignationSizeClass = () => {
        if (isFromSubordinate) return "text-xs";
        return "text-sm";
    };

    const maxLength = isFromSubordinate ? 12 : isFromInbox ? 15 : 20;

    // Skeleton component
    const SkeletonLoader = () => (
        <div className="flex items-center gap-2 animate-pulse">
            {!profileWrap && (
                <div className={`
                    ${getAvatarSizeClass()} 
                    rounded-full 
                    bg-gray-200 dark:bg-gray-700
                    border border-gray-200 dark:border-gray-600
                `} />
            )}

            <div className="flex-1 min-w-0 space-y-2">
                <div className="flex items-center justify-between gap-2">
                    <div className="flex-1 min-w-0">
                        {/* Name skeleton */}
                        <div className={`
                            h-4 bg-gray-200 dark:bg-gray-700 rounded 
                            ${getNameSizeClass() === 'text-sm' ? 'w-24' : 'w-32'}
                        `} />
                    </div>

                    {icon && (
                        <div className="flex-shrink-0">
                            <div className="w-4 h-4 bg-gray-200 dark:bg-gray-700 rounded" />
                        </div>
                    )}
                </div>

                {/* Designation and employee code skeleton */}
                <div className="flex items-center gap-1">
                    <div className={`
                        h-3 bg-gray-200 dark:bg-gray-700 rounded
                        ${getDesignationSizeClass() === 'text-xs' ? 'w-20' : 'w-24'}
                    `} />
                    <div className="w-1 h-3 bg-gray-300 dark:bg-gray-600" />
                    <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-16" />
                </div>
            </div>
        </div>
    );

    // Check if data is available
    const hasData = details && (fullname || designation || employee_code);

    // Show skeleton if loading or no data
    if (loading || !hasData) {
        return <SkeletonLoader />;
    }

    return (
        <div
            className={`flex items-center gap-2 ${onClick ? 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 rounded-md p-1 transition-colors' : ''}`}
            onClick={onClick}
            title={[fullname, designation, employee_code].filter(Boolean).join(" | ")}
        >
            {!profileWrap && (
                <img
                    src={profile}
                    alt={fullname}
                    className={`${getAvatarSizeClass()} rounded-full object-cover border border-gray-200 dark:border-gray-600`}
                />
            )}

            <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                    <div className="flex-1 min-w-0">
                        <p className={`
                            text-${size}
                            font-medium text-gray-900 dark:text-gray-100 truncate
                            ${getNameSizeClass()}
                            ${!is_active ? 'text-gray-400 dark:text-gray-500' : ''}
                        `}>
                            {fullname.length > maxLength ? (
                                <span
                                    className="cursor-help border-b border-dotted border-gray-300 dark:border-gray-600"
                                    title={fullname}
                                >
                                    {fullname.slice(0, maxLength)}...
                                </span>
                            ) : fullname}
                        </p>
                    </div>

                    {icon && (
                        <div className="flex-shrink-0 text-gray-400 dark:text-gray-500">
                            {icon}
                        </div>
                    )}
                </div>

                <p className={`
                    text-gray-600 dark:text-gray-400 mt-0.5 truncate
                    text-xs
                    ${getDesignationSizeClass()}
                    ${!is_active ? 'text-gray-400 dark:text-gray-500' : ''}
                `}>
                    {designation && (
                        <>
                            {designation.length > maxLength ? (
                                <span
                                    className={`cursor-help border-b border-dotted border-gray-300 dark:border-gray-600`}
                                    title={designation}
                                >
                                    {designation.slice(0, maxLength)}...
                                </span>
                            ) : designation}

                            {employee_code && (
                                <>
                                    <span className="text-gray-400 dark:text-gray-500 mx-1">|</span>
                                    {employee_code.length > 10 ? (
                                        <span
                                            className={`cursor-help border-b border-dotted border-gray-300 dark:border-gray-600`}
                                            title={employee_code}
                                        >
                                            {employee_code.slice(0, 10)}...
                                        </span>
                                    ) : employee_code}
                                </>
                            )}
                        </>
                    )}

                    {!designation && employee_code && (
                        employee_code.length > 10 ? (
                            <span
                                className="cursor-help border-b border-dotted border-gray-300 dark:border-gray-600"
                                title={employee_code}
                            >
                                {employee_code.slice(0, 10)}...
                            </span>
                        ) : employee_code
                    )}
                </p>
            </div>
        </div>
    );
};